// Mobile-only (<640px) filter trigger + bottom sheet. Search stays outside
// the sheet (always visible on the results screen); everything else
// (primary + secondary merged — no desktop density split, since there's no
// space-pressure difference between them once everything scrolls) lives
// inside with local-draft Apply/Cancel semantics: editing here does not
// touch the live URL until "Apply filters" is pressed, so intermediate
// selections don't re-fetch the results list underneath on every tap.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { SlidersHorizontal, X } from 'lucide-react';
import { Button } from '@siliconx/shared/ui/Button';
import {
  LifecycleField,
  ConditionField,
  ManufacturerField,
  ModelField,
  LocationField,
  LotField,
  TeamField,
  MissingFinancialsToggle,
} from './FilterFields';
import { useModalTrap } from './useModalTrap';
import type { FilterBarProps } from './filterTypes';
import { OrgFeature } from '../../lib/orgFeatures';

interface DraftState {
  lifecycleFilter: string;
  conditionFilter: string;
  manufacturerFilter: string;
  modelDraft: string;
  locationFilter: string;
  lotFilter: string;
  teamFilter: string;
  missingFinancialsOnly: boolean;
}

const EMPTY_DRAFT: DraftState = {
  lifecycleFilter: '',
  conditionFilter: '',
  manufacturerFilter: '',
  modelDraft: '',
  locationFilter: '',
  lotFilter: '',
  teamFilter: '',
  missingFinancialsOnly: false,
};

function toDraft(p: FilterBarProps): DraftState {
  return {
    lifecycleFilter: p.lifecycleFilter,
    conditionFilter: p.conditionFilter,
    manufacturerFilter: p.manufacturerFilter,
    modelDraft: p.modelDraft,
    locationFilter: p.locationFilter,
    lotFilter: p.lotFilter,
    teamFilter: p.teamFilter,
    missingFinancialsOnly: p.missingFinancialsOnly,
  };
}

export function MobileFilterSheet(props: FilterBarProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DraftState>(() => toDraft(props));
  const panelRef = useModalTrap(open);

  const activeCount = props.activeChips.length;

  const openSheet = () => {
    setDraft(toDraft(props));
    setOpen(true);
  };

  const cancel = () => { setOpen(false); };

  const apply = () => {
    props.setLifecycleFilter(draft.lifecycleFilter);
    props.setConditionFilter(draft.conditionFilter);
    props.setManufacturerFilter(draft.manufacturerFilter);
    props.setModelDraft(draft.modelDraft);
    props.setLocationFilter(draft.locationFilter);
    props.setLotFilter(draft.lotFilter);
    props.setTeamFilter(draft.teamFilter);
    props.setMissingFinancialsOnly(draft.missingFinancialsOnly);
    setOpen(false);
  };

  const resetDraft = () => { setDraft(EMPTY_DRAFT); };

  // Escape maps to Cancel semantics (discards the draft) — useModalTrap
  // doesn't own Escape itself since Cancel/Apply/backdrop all need the same
  // `open -> false` transition and Escape is just a fourth path to it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancel();
    };
    window.addEventListener('keydown', onKey);
    return () => { window.removeEventListener('keydown', onKey); };
  }, [open]);

  // Draft-scoped view of props so the shared Field atoms write into local
  // `draft` state instead of the live parent state while the sheet is open.
  const draftProps: FilterBarProps = {
    ...props,
    lifecycleFilter: draft.lifecycleFilter,
    setLifecycleFilter: (v) => { setDraft((d) => ({ ...d, lifecycleFilter: v })); },
    conditionFilter: draft.conditionFilter,
    setConditionFilter: (v) => { setDraft((d) => ({ ...d, conditionFilter: v })); },
    manufacturerFilter: draft.manufacturerFilter,
    setManufacturerFilter: (v) => { setDraft((d) => ({ ...d, manufacturerFilter: v })); },
    modelDraft: draft.modelDraft,
    setModelDraft: (v) => { setDraft((d) => ({ ...d, modelDraft: v })); },
    locationFilter: draft.locationFilter,
    setLocationFilter: (v) => { setDraft((d) => ({ ...d, locationFilter: v })); },
    lotFilter: draft.lotFilter,
    setLotFilter: (v) => { setDraft((d) => ({ ...d, lotFilter: v })); },
    teamFilter: draft.teamFilter,
    setTeamFilter: (v) => { setDraft((d) => ({ ...d, teamFilter: v })); },
    missingFinancialsOnly: draft.missingFinancialsOnly,
    setMissingFinancialsOnly: (v) => { setDraft((d) => ({ ...d, missingFinancialsOnly: v })); },
  };

  return (
    <div className="sm:hidden">
      <button
        type="button"
        onClick={openSheet}
        className="inline-flex h-11 items-center gap-1.5 rounded-md border border-border bg-card px-3 text-xs font-medium text-foreground"
      >
        <SlidersHorizontal className="size-3.5" />
        {activeCount > 0 ? `Filters (${activeCount})` : 'Filters'}
      </button>

      {open && createPortal(
        <div className="fixed inset-0 z-50">
          {/* Backdrop click handler lives on this inner div, not the outer
              fixed wrapper — the wrapper also contains the panel, so a
              click handler on it would fire for clicks inside the panel too. */}
          <div className="absolute inset-0 bg-black/40" onClick={cancel} />
          <div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Filters"
            tabIndex={-1}
            className="absolute inset-x-0 bottom-0 flex max-h-[85vh] flex-col rounded-t-xl bg-card shadow-lg outline-none"
          >
            <div className="flex items-center justify-center pt-2">
              <div className="h-1 w-10 rounded-full bg-muted" />
            </div>
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <span className="text-sm font-semibold text-foreground">Filters</span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={resetDraft}
                  className="tap-target-44 text-xs text-muted-foreground hover:text-foreground"
                >
                  Reset
                </button>
                <button
                  type="button"
                  onClick={cancel}
                  aria-label="Close"
                  className="tap-target-44 text-muted-foreground hover:text-foreground"
                >
                  <X className="size-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-3">
              <LifecycleField {...draftProps} />
              <ConditionField {...draftProps} />
              <ManufacturerField {...draftProps} />
              <ModelField {...draftProps} />
              {/* SIL-1035: see FilterBar.tsx's matching comment. */}
              <OrgFeature name="locations">
                <LocationField {...draftProps} />
              </OrgFeature>
              <OrgFeature name="lots">
                <LotField {...draftProps} />
              </OrgFeature>
              {props.isAdmin && (
                <OrgFeature name="teams">
                  <TeamField {...draftProps} />
                </OrgFeature>
              )}
              <OrgFeature name="finance">
                <MissingFinancialsToggle {...draftProps} />
              </OrgFeature>
            </div>
            <div className="flex items-center gap-2 border-t border-border px-4 py-3">
              <Button variant="secondary" size="sm" className="h-11 flex-1" onClick={cancel}>
                Cancel
              </Button>
              <Button size="sm" className="h-11 flex-1" onClick={apply}>
                Apply filters
              </Button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
